/**
 * Telegram Mini App SDK wrapper (lightweight, no external dep).
 *
 * Tránh dùng `@twa-dev/sdk` để bundle nhỏ và full control type.
 *
 * Quy tắc:
 *   - Mọi access `window.Telegram.WebApp` phải qua wrapper này.
 *   - Hoạt động "noop graceful" khi chạy browser thường (không có Telegram) —
 *     tất cả method trả default an toàn, không throw.
 *
 * Tham chiếu: https://core.telegram.org/bots/webapps
 */

export interface TelegramThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  section_header_text_color?: string;
  destructive_text_color?: string;
}

export type TelegramColorScheme = "light" | "dark";

export type HapticImpactStyle = "light" | "medium" | "heavy" | "rigid" | "soft";
export type HapticNotificationType = "error" | "success" | "warning";

interface TelegramMainButton {
  text: string;
  isVisible: boolean;
  isActive: boolean;
  isProgressVisible: boolean;
  setText(text: string): void;
  show(): void;
  hide(): void;
  enable(): void;
  disable(): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
  onClick(callback: () => void): void;
  offClick(callback: () => void): void;
  setParams(params: {
    text?: string;
    color?: string;
    text_color?: string;
    is_active?: boolean;
    is_visible?: boolean;
  }): void;
}

interface TelegramBackButton {
  isVisible: boolean;
  show(): void;
  hide(): void;
  onClick(callback: () => void): void;
  offClick(callback: () => void): void;
}

interface TelegramHapticFeedback {
  impactOccurred(style: HapticImpactStyle): void;
  notificationOccurred(type: HapticNotificationType): void;
  selectionChanged(): void;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
      is_premium?: boolean;
    };
    auth_date?: number;
    hash?: string;
  };
  version: string;
  platform: string;
  colorScheme: TelegramColorScheme;
  themeParams: TelegramThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor: string;
  backgroundColor: string;
  isClosingConfirmationEnabled: boolean;
  BackButton: TelegramBackButton;
  MainButton: TelegramMainButton;
  HapticFeedback: TelegramHapticFeedback;
  ready(): void;
  expand(): void;
  close(): void;
  enableClosingConfirmation(): void;
  disableClosingConfirmation(): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  onEvent(eventType: string, callback: () => void): void;
  offEvent(eventType: string, callback: () => void): void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

/** Returns WebApp instance hoặc null nếu không chạy trong Telegram. */
function getWebApp(): TelegramWebApp | null {
  if (typeof window === "undefined") return null;
  return window.Telegram?.WebApp ?? null;
}

/**
 * True nếu app đang chạy bên trong Telegram Mini App.
 * KHÔNG cache — Telegram inject script async, gọi lại để re-check.
 */
export function isTelegramMiniApp(): boolean {
  const wa = getWebApp();
  // initData rỗng → mở qua link bình thường, không phải mini app real.
  return wa !== null && wa.initData.length > 0;
}

/** Raw initData query-string để gửi backend verify HMAC. */
export function getInitData(): string {
  return getWebApp()?.initData ?? "";
}

/** User parsed (chưa verify HMAC — chỉ để FE hiển thị). */
export function getInitDataUnsafe() {
  return getWebApp()?.initDataUnsafe;
}

/** Telegram language code (vd "vi", "en", "zh-hans"). */
export function getTelegramLanguageCode(): string | null {
  return getWebApp()?.initDataUnsafe?.user?.language_code ?? null;
}

/** Map Telegram language_code → locale ta support. */
export function mapTelegramLangToLocale(
  tgLang: string | null,
): "vi" | "en" | "zh-CN" | "ru" {
  if (!tgLang) return "vi";
  const lower = tgLang.toLowerCase();
  if (lower.startsWith("zh")) return "zh-CN";
  if (lower === "en") return "en";
  if (lower === "ru") return "ru";
  if (lower === "vi") return "vi";
  return "vi";
}

/** Theme params (bg/text color etc) — fallback empty nếu không Telegram. */
export function getThemeParams(): TelegramThemeParams {
  return getWebApp()?.themeParams ?? {};
}

export function getColorScheme(): TelegramColorScheme {
  return getWebApp()?.colorScheme ?? "light";
}

/**
 * Init app trong TMA mode: ready() + expand() fullscreen + apply theme.
 * Idempotent — gọi nhiều lần không sao.
 */
export function initTelegramApp(): void {
  const wa = getWebApp();
  if (!wa) return;
  try {
    wa.ready();
    if (!wa.isExpanded) wa.expand();
  } catch {
    // ignore — version cũ có thể thiếu method
  }
}

// ─── Haptic helpers (no-op outside Telegram) ───

export function hapticImpact(style: HapticImpactStyle = "light"): void {
  try {
    getWebApp()?.HapticFeedback.impactOccurred(style);
  } catch {
    // ignore
  }
}

export function hapticNotification(type: HapticNotificationType): void {
  try {
    getWebApp()?.HapticFeedback.notificationOccurred(type);
  } catch {
    // ignore
  }
}

export function hapticSelection(): void {
  try {
    getWebApp()?.HapticFeedback.selectionChanged();
  } catch {
    // ignore
  }
}

// ─── BackButton helpers ───

export function showBackButton(onClick: () => void): () => void {
  const wa = getWebApp();
  if (!wa) return () => {};
  wa.BackButton.onClick(onClick);
  wa.BackButton.show();
  return () => {
    wa.BackButton.offClick(onClick);
    wa.BackButton.hide();
  };
}

// ─── MainButton helpers ───

export interface MainButtonConfig {
  text: string;
  color?: string;
  textColor?: string;
  isActive?: boolean;
  showProgress?: boolean;
}

export function showMainButton(
  config: MainButtonConfig,
  onClick: () => void,
): () => void {
  const wa = getWebApp();
  if (!wa) return () => {};
  wa.MainButton.setParams({
    text: config.text,
    color: config.color,
    text_color: config.textColor,
    is_active: config.isActive ?? true,
    is_visible: true,
  });
  if (config.showProgress) {
    wa.MainButton.showProgress(true);
  } else {
    wa.MainButton.hideProgress();
  }
  wa.MainButton.onClick(onClick);
  return () => {
    wa.MainButton.offClick(onClick);
    wa.MainButton.hide();
    wa.MainButton.hideProgress();
  };
}

export function hideMainButton(): void {
  getWebApp()?.MainButton.hide();
}

// ─── Misc ───

export function enableCloseConfirmation(): void {
  try {
    getWebApp()?.enableClosingConfirmation();
  } catch {
    // ignore
  }
}

export function getTelegramPlatform(): string | null {
  return getWebApp()?.platform ?? null;
}
